import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  calculateExpressFee,
  getExpressOrderDetail,
  listExpressRoutes,
  submitExpressOrderV5,
} from "../endpoints/express";
import { useBackendLang } from "../lang-context";
import type { ExpressCalc, ExpressOrderDetail, ExpressSubmitParam } from "../types/express";
import type { LogisticsRoute } from "../types/logistics";

type Currency = "RUB" | "CNY" | "USD";

const ctx = { baseUrl: API_BASE_URL };

/**
 * Read-only express-order detail (item recap + bound recipient address). Loaded
 * when the submit-parcel page mounts with `?expressOrderNo=`. `lang` is in the
 * key so a locale switch refetches localized address text (titles stay raw).
 */
export function useExpressOrderDetail(authToken: string | null, expressOrderNo: string | null) {
  const lang = useBackendLang();
  return useQuery<ExpressOrderDetail>({
    queryKey: ["express", "detail", expressOrderNo, lang],
    enabled: !!authToken && !!expressOrderNo,
    queryFn: () => {
      if (!authToken || !expressOrderNo) throw new Error("NO_TOKEN_OR_ORDER");
      return getExpressOrderDetail(ctx, authToken, expressOrderNo, { lang });
    },
    staleTime: 30_000,
  });
}

/**
 * Available routes for an express order bound to `addressId` + the currently
 * selected packing services (services change billable weight → fees recompute).
 * Re-keyed on the service selection so toggling a service refetches routes.
 */
export function useExpressRoutes(
  authToken: string | null,
  input: { expressOrderNo: string | null; addressId: string | null; serviceItemIdList: string[] },
  currency: Currency,
) {
  const lang = useBackendLang();
  const svcKey = [...input.serviceItemIdList].sort().join(",");
  return useQuery<LogisticsRoute[]>({
    queryKey: ["express", "routes", input.expressOrderNo, input.addressId, svcKey, currency, lang],
    enabled: !!authToken && !!input.expressOrderNo && !!input.addressId,
    queryFn: () => {
      if (!authToken || !input.expressOrderNo || !input.addressId) {
        throw new Error("NO_TOKEN_OR_ORDER");
      }
      return listExpressRoutes(
        ctx,
        authToken,
        {
          expressOrderNo: input.expressOrderNo,
          addressId: input.addressId,
          serviceItemIdList: input.serviceItemIdList,
        },
        { lang, currency },
      );
    },
    staleTime: 0,
  });
}

/**
 * Live fee breakdown for the current route/service/insurance/coupon selection.
 * A mutation (fired on every selection change), not an ambient query — the
 * submit-parcel page debounces and re-runs it as the user toggles options.
 */
export function useCalculateExpressFee(authToken: string | null, currency: Currency) {
  const lang = useBackendLang();
  return useMutation<ExpressCalc, Error, ExpressSubmitParam>({
    mutationFn: (param) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return calculateExpressFee(ctx, authToken, param, { lang, currency });
    },
  });
}

/**
 * Finalize the waybill (`/express/submitV5`). Resolves to the express order
 * number to pay. On success the warehouse stored list + user stats are
 * invalidated (the shipped items leave the bucket) and the express-order list
 * refreshes.
 */
export function useSubmitExpressOrder(authToken: string | null, currency: Currency) {
  const lang = useBackendLang();
  const qc = useQueryClient();
  return useMutation<string, Error, ExpressSubmitParam>({
    mutationFn: (param) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return submitExpressOrderV5(ctx, authToken, param, { lang, currency });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouse", "stored"] });
      qc.invalidateQueries({ queryKey: ["user", "statistics"] });
      qc.invalidateQueries({ queryKey: ["express", "list"] });
    },
  });
}
